import type { ModuleId } from "../auth/config.ts";
import { putRun, getRun, appendLog, type RunRecord, type RunStatus } from "../storage/idb.ts";

/**
 * Run lifecycle helpers, shared across export modules (ARCHITECTURE.md §7).
 *
 * A run is the top-level unit resume operates on: one runId groups every
 * cursor, item and log line a module writes. Reusing the same runId across
 * sessions is what makes "close the tab, come back tomorrow" resume rather
 * than restart.
 */

/** One run per destination folder, not one per session — see startOrResumeRun(). */
const CURRENT_RUN_KEY = "o365x.currentRunId";

function newRunId(): string {
    return crypto.randomUUID();
}

/**
 * Finds the run tied to the current destination folder, creating one if this
 * is the first export to it. localStorage rather than IndexedDB because it
 * must be readable synchronously before the first await — the id is needed to
 * even query IndexedDB for the run record.
 */
export async function startOrResumeRun(modules: ModuleId[]): Promise<RunRecord> {
    const existingId = localStorage.getItem(CURRENT_RUN_KEY);
    if (existingId) {
        const existing = await getRun(existingId);
        if (existing) {
            // Extend rather than replace: a resumed run may add a module the
            // first session did not select.
            const merged: RunRecord = {
                ...existing,
                modules: Array.from(new Set([...existing.modules, ...modules])),
                status: "running"
            };
            await putRun(merged);
            return merged;
        }
    }

    const run: RunRecord = {
        runId: newRunId(),
        startedAt: new Date().toISOString(),
        modules,
        options: {},
        status: "running"
    };
    localStorage.setItem(CURRENT_RUN_KEY, run.runId);
    await putRun(run);
    return run;
}

/**
 * The run tied to the current destination folder, if one exists.
 *
 * Read-only — unlike startOrResumeRun() this creates nothing, so the UI can
 * ask "is there unfinished work here?" on load without starting a run as a
 * side effect of asking.
 */
export async function getCurrentRun(): Promise<RunRecord | null> {
    const id = localStorage.getItem(CURRENT_RUN_KEY);
    if (!id) {
        return null;
    }
    return (await getRun(id)) ?? null;
}

export async function setRunStatus(runId: string, status: RunStatus): Promise<void> {
    const run = await getRun(runId);
    if (run) {
        await putRun({ ...run, status });
    }
}

/** Clears the run pointer. Call when the user picks a different destination folder. */
export function forgetCurrentRun(): void {
    localStorage.removeItem(CURRENT_RUN_KEY);
}

export interface RunLogger {
    info(message: string): Promise<void>;
    warn(message: string): Promise<void>;
    error(message: string): Promise<void>;
}

/**
 * Scoped logger for one run/module pair.
 *
 * Never pass subjects, filenames, addresses or content through this — the log
 * is written to disk and handed to support verbatim (§14). Status codes,
 * counts and endpoint paths are fine; GraphError.toLogString() already strips
 * query strings for exactly this reason.
 */
export function runLogger(runId: string, module: ModuleId | "app"): RunLogger {
    const write = (level: "info" | "warn" | "error", message: string) =>
        appendLog({ runId, level, module, message });

    return {
        info: (message) => write("info", message),
        warn: (message) => write("warn", message),
        error: (message) => write("error", message)
    };
}
